import { Layers } from "lucide-react";

export function meta() {
  return [{ title: "Privacy Policy — Orchestrate" }];
}

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy">
      <Section title="1. Introduction">
        <p>
          APTIM-Solutions Ltd (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is committed to
          protecting and respecting your privacy. This Privacy Policy explains how we collect, use,
          store, and share your personal data when you use the Orchestrate platform
          (&quot;Service&quot;).
        </p>
        <p>
          APTIM-Solutions Ltd is the data controller for the purposes of the UK General Data
          Protection Regulation (UK GDPR) and the Data Protection Act 2018. Where we process
          personal data of individuals in the European Economic Area (EEA), we comply with the EU
          General Data Protection Regulation (EU GDPR).
        </p>
      </Section>

      <Section title="2. Data We Collect">
        <p>We collect and process the following categories of personal data:</p>
        <ul>
          <li>
            <strong>Account information:</strong> Full name, email address, and organisation
            membership when you register or are invited to Orchestrate.
          </li>
          <li>
            <strong>Authentication data:</strong> We use Auth0 as our identity provider. We do not
            store passwords. Auth0&apos;s privacy practices are governed by their own policy.
          </li>
          <li>
            <strong>Usage data:</strong> Actions performed within the platform (e.g. creating
            projects, updating RAID items) are recorded in an audit trail for governance purposes.
          </li>
          <li>
            <strong>Analytics data:</strong> We use Microsoft Clarity for session analytics
            (heatmaps, session recordings) to improve the user experience. Clarity collects
            anonymised interaction data. No personally identifiable information is captured by
            Clarity. Clarity is only activated if you consent to analytics cookies.
          </li>
          <li>
            <strong>AI interaction data:</strong> When you use AI-powered features (such as the
            project assistant), relevant project context is sent to Anthropic&apos;s Claude API to
            generate responses. We log the prompts sent and tokens used for usage tracking and cost
            management purposes. Anthropic does not use API inputs for model training.
          </li>
          <li>
            <strong>Error and performance data:</strong> We use Sentry for error monitoring and
            performance tracking. Sentry automatically collects technical data such as browser type,
            operating system, error stack traces, page URLs, and session identifiers when errors
            occur. This helps us identify and resolve issues quickly.
          </li>
          <li>
            <strong>Feedback data:</strong> When you submit feedback via the in-app widget, we
            collect your message, the page you were on, and your user ID.
          </li>
          <li>
            <strong>External resources:</strong> Organisation administrators may add external team
            members (non-users) with a name and optional email address for task assignment purposes.
          </li>
        </ul>
      </Section>

      <Section title="3. How We Use Your Data">
        <p>We process your personal data for the following purposes:</p>
        <ul>
          <li>
            <strong>Service delivery:</strong> To provide, maintain, and improve the Orchestrate
            platform, including user authentication, organisation management, and project governance
            features.
          </li>
          <li>
            <strong>Audit and compliance:</strong> To maintain audit trails of actions taken within
            projects for governance, accountability, and regulatory compliance.
          </li>
          <li>
            <strong>Communication:</strong> To send transactional emails such as project
            invitations, gate notifications, and system alerts.
          </li>
          <li>
            <strong>Analytics:</strong> To understand how users interact with the platform and to
            improve functionality and user experience.
          </li>
          <li>
            <strong>AI-powered features:</strong> To provide intelligent suggestions, project
            assistance, and content generation using Anthropic&apos;s Claude API. AI features
            process project data only when explicitly invoked by the user.
          </li>
          <li>
            <strong>Error monitoring:</strong> To detect, diagnose, and resolve technical issues
            using Sentry, ensuring the reliability and performance of the platform.
          </li>
        </ul>
        <p>
          The legal bases for processing are: performance of a contract (providing the Service),
          legitimate interests (improving our product, maintaining security), and compliance with
          legal obligations (audit trails, data retention).
        </p>
      </Section>

      <Section title="4. Data Sharing">
        <p>We do not sell your personal data. We share data only with:</p>
        <ul>
          <li>
            <strong>Auth0 (Okta):</strong> Identity and authentication provider. Processes
            authentication credentials and session data.
          </li>
          <li>
            <strong>Anthropic:</strong> AI service provider (Claude API). Receives project context
            data when AI features are used. Anthropic does not use API inputs to train models.
            See{" "}
            <a
              href="https://www.anthropic.com/policies/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-brand hover:underline"
            >
              Anthropic&apos;s Privacy Policy
            </a>
            .
          </li>
          <li>
            <strong>Microsoft Clarity:</strong> Anonymised session analytics (heatmaps, session
            recordings). Only activated with user consent. See{" "}
            <a
              href="https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-consent"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-brand hover:underline"
            >
              Clarity&apos;s privacy documentation
            </a>
            .
          </li>
          <li>
            <strong>Sentry:</strong> Error monitoring and performance tracking. Collects technical
            diagnostic data when errors occur. See{" "}
            <a
              href="https://sentry.io/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-brand hover:underline"
            >
              Sentry&apos;s Privacy Policy
            </a>
            .
          </li>
          <li>
            <strong>Resend:</strong> Transactional email delivery (invitations, notifications).
          </li>
          <li>
            <strong>Hosting providers:</strong> Our infrastructure is hosted on Fly.io with
            databases in the EU region.
          </li>
        </ul>
        <p>
          All third-party processors are bound by data processing agreements and are required to
          protect your data in accordance with applicable data protection law.
        </p>
      </Section>

      <Section title="5. Data Retention">
        <p>
          We retain your personal data for as long as your account is active or as needed to provide
          the Service. Audit trail data is retained for the lifetime of the project for governance
          and compliance purposes. If you request account deletion, we will anonymise your personal
          data within 30 days while preserving the integrity of project records.
        </p>
      </Section>

      <Section title="6. Data Security">
        <p>
          We implement appropriate technical and organisational measures to protect your personal
          data, including:
        </p>
        <ul>
          <li>Encryption in transit (TLS 1.2+) and at rest</li>
          <li>Multi-tenant data isolation with strict organisation boundaries</li>
          <li>Role-based access control at platform, organisation, and project levels</li>
          <li>Regular security reviews and dependency updates</li>
        </ul>
      </Section>

      <Section title="7. International Transfers">
        <p>
          Our primary infrastructure is hosted in the EU. Where data is transferred outside the UK
          or EEA (e.g. to US-based sub-processors), we ensure appropriate safeguards are in place,
          such as Standard Contractual Clauses (SCCs) or the sub-processor&apos;s participation in
          recognised data transfer frameworks.
        </p>
      </Section>

      <Section title="8. Your Rights">
        <p>Under UK GDPR and EU GDPR, you have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Rectify inaccurate or incomplete data</li>
          <li>Request erasure of your data (right to be forgotten)</li>
          <li>Restrict or object to processing</li>
          <li>Data portability — receive your data in a structured, machine-readable format</li>
          <li>Withdraw consent at any time (where processing is based on consent)</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at{" "}
          <a
            href="mailto:privacy@aptim-solutions.com"
            className="text-accent-brand hover:underline"
          >
            privacy@aptim-solutions.com
          </a>
          . We will respond within 30 days.
        </p>
      </Section>

      <Section title="9. Cookies and Similar Technologies">
        <p>
          Orchestrate uses cookies and similar technologies to provide, secure, and improve the
          Service. The table below lists all cookies and storage mechanisms used:
        </p>

        <h3 className="text-base font-bold text-navy mt-6 mb-3">Essential Cookies</h3>
        <p>
          These cookies are strictly necessary for the platform to function. They cannot be disabled
          without breaking core functionality. No consent is required under ICO guidance for strictly
          necessary cookies.
        </p>
        <table className="w-full text-sm border-collapse mt-3 mb-6">
          <thead>
            <tr className="border-b border-border-light text-left">
              <th className="py-2 pr-4 font-bold text-navy">Cookie</th>
              <th className="py-2 pr-4 font-bold text-navy">Provider</th>
              <th className="py-2 pr-4 font-bold text-navy">Purpose</th>
              <th className="py-2 font-bold text-navy">Duration</th>
            </tr>
          </thead>
          <tbody className="[&_tr]:border-b [&_tr]:border-border-light">
            <tr>
              <td className="py-2 pr-4"><code>appSession</code></td>
              <td className="py-2 pr-4">Auth0 (Okta)</td>
              <td className="py-2 pr-4">
                Encrypted session cookie for user authentication. Contains no personal data in
                readable form.
              </td>
              <td className="py-2">24h rolling, 30 days max</td>
            </tr>
            <tr>
              <td className="py-2 pr-4"><code>orch_org</code></td>
              <td className="py-2 pr-4">Orchestrate</td>
              <td className="py-2 pr-4">
                Stores the user&apos;s currently selected organisation ID for multi-tenant routing.
              </td>
              <td className="py-2">Session</td>
            </tr>
            <tr>
              <td className="py-2 pr-4"><code>orch_consent</code></td>
              <td className="py-2 pr-4">Orchestrate</td>
              <td className="py-2 pr-4">
                Records the user&apos;s cookie consent preference (accepted or rejected).
              </td>
              <td className="py-2">1 year</td>
            </tr>
          </tbody>
        </table>

        <h3 className="text-base font-bold text-navy mt-6 mb-3">Analytics Cookies</h3>
        <p>
          These cookies are only set if you accept analytics cookies via the cookie consent banner.
          You can change your preference at any time by clearing the <code>orch_consent</code>{" "}
          cookie in your browser settings, which will re-display the consent banner on your next
          visit.
        </p>
        <table className="w-full text-sm border-collapse mt-3 mb-6">
          <thead>
            <tr className="border-b border-border-light text-left">
              <th className="py-2 pr-4 font-bold text-navy">Cookie</th>
              <th className="py-2 pr-4 font-bold text-navy">Provider</th>
              <th className="py-2 pr-4 font-bold text-navy">Purpose</th>
              <th className="py-2 font-bold text-navy">Duration</th>
            </tr>
          </thead>
          <tbody className="[&_tr]:border-b [&_tr]:border-border-light">
            <tr>
              <td className="py-2 pr-4"><code>_clck</code></td>
              <td className="py-2 pr-4">Microsoft Clarity</td>
              <td className="py-2 pr-4">Persists the Clarity user ID for session analytics.</td>
              <td className="py-2">1 year</td>
            </tr>
            <tr>
              <td className="py-2 pr-4"><code>_clsk</code></td>
              <td className="py-2 pr-4">Microsoft Clarity</td>
              <td className="py-2 pr-4">
                Connects page views into a single Clarity session recording.
              </td>
              <td className="py-2">1 day</td>
            </tr>
            <tr>
              <td className="py-2 pr-4"><code>CLID</code></td>
              <td className="py-2 pr-4">Microsoft Clarity</td>
              <td className="py-2 pr-4">
                Identifies the first-time Clarity saw this user. Used for heatmap and recording
                analytics.
              </td>
              <td className="py-2">1 year</td>
            </tr>
            <tr>
              <td className="py-2 pr-4"><code>ANONCHK</code></td>
              <td className="py-2 pr-4">Microsoft Clarity</td>
              <td className="py-2 pr-4">
                Indicates whether MUID cookie consent was transferred. Set by Clarity.
              </td>
              <td className="py-2">10 minutes</td>
            </tr>
            <tr>
              <td className="py-2 pr-4"><code>MR</code>, <code>MUID</code>, <code>SM</code></td>
              <td className="py-2 pr-4">Microsoft Clarity</td>
              <td className="py-2 pr-4">
                Microsoft tracking identifiers used by Clarity for anonymised analytics. No personal
                data is captured.
              </td>
              <td className="py-2">Various (up to 1 year)</td>
            </tr>
          </tbody>
        </table>

        <h3 className="text-base font-bold text-navy mt-6 mb-3">Error Monitoring</h3>
        <p>
          Sentry uses browser local storage (not cookies) to store session replay identifiers and
          error context. This data is technical in nature and is used solely for diagnosing and
          resolving platform issues. It does not contain personally identifiable information.
        </p>

        <p className="mt-6">
          We do not use advertising cookies or third-party tracking cookies. No data is shared with
          advertisers.
        </p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any material
          changes by posting the new policy on this page and updating the &quot;last updated&quot;
          date. We encourage you to review this policy periodically.
        </p>
      </Section>

      <Section title="11. Contact Us">
        <p>
          If you have any questions about this Privacy Policy or our data practices, please contact
          us:
        </p>
        <ul>
          <li>
            Email:{" "}
            <a
              href="mailto:privacy@aptim-solutions.com"
              className="text-accent-brand hover:underline"
            >
              privacy@aptim-solutions.com
            </a>
          </li>
          <li>APTIM-Solutions Ltd</li>
          <li>Registered in England and Wales</li>
        </ul>
        <p>
          You also have the right to lodge a complaint with the Information Commissioner&apos;s
          Office (ICO) at{" "}
          <a
            href="https://ico.org.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-brand hover:underline"
          >
            ico.org.uk
          </a>{" "}
          if you believe your data protection rights have been violated.
        </p>
      </Section>
    </LegalPage>
  );
}

// ─── Shared layout components ───

/** Single place to update when any legal page changes */
const LEGAL_LAST_UPDATED = "3 April 2026";

function LegalPage({
  title,
  lastUpdated = LEGAL_LAST_UPDATED,
  children,
}: {
  title: string;
  lastUpdated?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b border-border-light bg-white">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-navy rounded-lg flex items-center justify-center">
              <Layers className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-lg font-bold text-navy tracking-tight">Orchestrate</span>
          </a>
          <a href="/" className="text-sm text-text-secondary hover:text-navy transition-colors">
            &larr; Back to home
          </a>
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-extrabold text-navy mb-2">{title}</h1>
        <p className="text-sm text-text-muted mb-8">Last updated: {lastUpdated}</p>
        <div className="prose prose-sm max-w-none text-text [&_h2]:text-navy [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1.5 [&_li]:leading-relaxed [&_code]:bg-surface [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs">
          {children}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function LegalFooter() {
  return (
    <footer className="py-8 px-6 border-t border-border-light bg-surface">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-navy rounded flex items-center justify-center">
            <Layers className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-xs font-bold text-navy">Orchestrate</span>
          <span className="text-[10px] text-text-muted ml-1">by APTIM-Solutions</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-text-muted">
          <a href="/privacy" className="hover:text-navy transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-navy transition-colors">
            Terms & Conditions
          </a>
          <a href="/dpa" className="hover:text-navy transition-colors">
            DPA
          </a>
          <a href="/company" className="hover:text-navy transition-colors">
            Company Details
          </a>
        </div>
        <span className="text-xs text-text-muted">
          &copy; {new Date().getFullYear()} APTIM-Solutions Ltd.
        </span>
      </div>
    </footer>
  );
}

export { LEGAL_LAST_UPDATED, LegalPage, Section, LegalFooter };
